"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { LucideIcon } from "lucide-react";
import { Loader2 } from "lucide-react";
import { AeraButton } from "./AeraButton";
import { ScrollRailNavButtons } from "./CardScrollRail";
import { useHorizontalScrollRail } from "./useHorizontalScrollRail";

export type SegmentedTabItem<T extends string = string> = {
  id: T;
  label: string;
  icon?: LucideIcon;
  /** Custom icon node; takes precedence over `icon`. */
  iconNode?: ReactNode;
  count?: number;
  /** When true, show a pulse placeholder instead of the count. */
  countLoading?: boolean;
  disabled?: boolean;
  trailing?: ReactNode;
};

export type SegmentedTabSwitcherProps<T extends string = string> = {
  tabs: SegmentedTabItem<T>[];
  activeTab: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
};

function joinClassNames(
  ...parts: Array<string | false | null | undefined>
): string {
  return parts.filter(Boolean).join(" ");
}

function TabCountBadge(props: {
  count?: number;
  loading?: boolean;
  tone?: "accent" | "muted";
}) {
  const tone = props.tone ?? "muted";

  if (props.loading) {
    return (
      <span
        className={joinClassNames(
          "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
          tone === "accent"
            ? "bg-aera-warmcream text-aera-amberdark"
            : "bg-aera-lightgray text-aera-charcoal",
        )}
        aria-hidden
      >
        <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" />
      </span>
    );
  }

  if (props.count == null) return null;

  return (
    <span
      className={joinClassNames(
        "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
        tone === "accent"
          ? "bg-aera-warmcream text-aera-amberdark"
          : "bg-aera-lightgray text-aera-charcoal",
      )}
    >
      {props.count.toLocaleString("th-TH")}
    </span>
  );
}

export function SegmentedTabSwitcher<T extends string>({
  tabs,
  activeTab,
  onChange,
  ariaLabel = "สลับแท็บ",
  className,
  style,
}: SegmentedTabSwitcherProps<T>) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const indicatorRailRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<
    Record<string, HTMLButtonElement | HTMLAnchorElement | null>
  >({});
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
  } | null>(null);
  const [indicatorReady, setIndicatorReady] = useState(false);

  const tabMeasureKey = tabs
    .map(
      (tab) =>
        `${tab.id}:${tab.label}:${tab.countLoading ? "loading" : (tab.count ?? "")}`,
    )
    .join("|");

  const { railRef, scrollHints, scrollByDirection } = useHorizontalScrollRail({
    railRef: scrollerRef,
    syncKey: `${activeTab}|${tabMeasureKey}`,
    itemSelector: '[role="tab"]',
  });

  const measure = () => {
    const rail = indicatorRailRef.current;
    const el = tabRefs.current[activeTab];
    if (!rail || !el) return;

    const railRect = rail.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    setIndicator({
      left: elRect.left - railRect.left,
      width: elRect.width,
    });
  };

  const revealActiveTab = () => {
    const scroller = scrollerRef.current;
    const el = tabRefs.current[activeTab];
    if (!scroller || !el) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const pad = 12;
    if (elRect.left < scrollerRect.left + pad) {
      scroller.scrollLeft -= scrollerRect.left + pad - elRect.left;
    } else if (elRect.right > scrollerRect.right - pad) {
      scroller.scrollLeft += elRect.right - (scrollerRect.right - pad);
    }
  };

  useLayoutEffect(() => {
    measure();
    revealActiveTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tabMeasureKey]);

  useEffect(() => {
    const rail = indicatorRailRef.current;
    if (!rail) return;

    const ro = new ResizeObserver(() => measure());
    ro.observe(rail);
    const el = tabRefs.current[activeTab];
    if (el) ro.observe(el);

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (indicator) setIndicatorReady(true);
  }, [indicator]);

  return (
    <div
      className={joinClassNames("relative min-w-0", className)}
      style={style}
    >
      <ScrollRailNavButtons
        scrollHints={scrollHints}
        onScrollLeft={() => scrollByDirection("left")}
        onScrollRight={() => scrollByDirection("right")}
        hideOnMobile={false}
        iconClassName="h-4 w-4 text-aera-gray"
        leftAriaLabel="แท็บก่อนหน้า"
        rightAriaLabel="แท็บถัดไป"
      />
      <div
        ref={railRef}
        className="flex h-full min-h-0 min-w-0 w-full overflow-x-auto overflow-y-hidden scrollbar-hide"
        role="tablist"
        aria-label={ariaLabel}
      >
      <div ref={indicatorRailRef} className="relative flex h-full shrink-0 items-stretch gap-6 pb-3">
        {indicator ? (
          <div
            aria-hidden
            className={joinClassNames(
              "pointer-events-none absolute bottom-0 z-10 h-[3px] bg-aera-amber transition-[transform,width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
              indicatorReady ? "opacity-100" : "opacity-0",
            )}
            style={{
              width: `${indicator.width}px`,
              transform: `translateX(${indicator.left}px)`,
            }}
          />
        ) : null}

        {tabs.map(({ id, label, icon: Icon, iconNode, count, countLoading, disabled, trailing }) => {
          const isActive = activeTab === id;
          const showCount = countLoading || count != null;
          const badgeTone = isActive ? "accent" : "muted";
          const ariaCount =
            countLoading || count == null
              ? undefined
              : count.toLocaleString("th-TH");

          return (
            <AeraButton
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={
                ariaCount != null ? `${label} ${ariaCount}` : label
              }
              variant="link"
              fontWeight={isActive ? "semibold" : "medium"}
              disabled={disabled}
              onClick={() => {
                if (!disabled) onChange(id);
              }}
              ref={(el) => {
                tabRefs.current[id] = el;
              }}
              className={joinClassNames(
                "group relative h-full whitespace-nowrap",
                !disabled && "cursor-pointer",
                isActive
                  ? "text-aera-charcoal"
                  : "text-aera-gray hover:text-aera-charcoal",
                disabled && "cursor-not-allowed opacity-40",
              )}
              icon={
                iconNode ??
                (Icon ? (
                  <span
                    className={joinClassNames(
                      "flex h-7 w-7 items-center justify-center rounded-lg transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]",
                      isActive ? "bg-aera-lightgray" : "bg-transparent",
                    )}
                  >
                    <Icon
                      className={joinClassNames(
                        "h-4 w-4 transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]",
                        isActive ? "text-aera-amber" : "group-hover:scale-105",
                      )}
                      aria-hidden
                    />
                  </span>
                ) : undefined)
              }
            >
              <span className="inline-flex items-center gap-2 pt-0.5">
                <span>{label}</span>
                {showCount ? (
                  <TabCountBadge
                    count={count}
                    loading={countLoading}
                    tone={badgeTone}
                  />
                ) : null}
              </span>
              {trailing}
            </AeraButton>
          );
        })}
      </div>
      </div>
    </div>
  );
}
